import React, { useMemo, useState } from 'react';
import { useAorText } from 'utils/aorTerminology';
import ReactECharts from 'echarts-for-react';
import { ChevronDown, Info } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import AssetExposureTable from './AssetExposureTable';
import RankedFeaturesTable from './RankedFeaturesTable';
import { MapFocusButton } from './mapFocus';
import {
  applyDemoResourceCapacityCountyBoost,
  DEMO_FLOOD_FILTER_OPTIONS,
  LAYER_STATS_DEMO_FILTERS_ENABLED,
  populationServiceCapacityRowsSorted,
  type DemoFloodFilterKey,
} from './layerStatsDemoFilters';
import {
  ALL_STATES_FILTER,
  CONFIDENCE_OVERALL_MIN_OPTIONS,
  countyPriorityBreakdownForStateOrAll,
  defaultCountyState,
  depthBreakdownForFema,
  femaTotalsFromCross,
  analyticsCardsToRender,
  FLOOD_OCCUPANCY_FIELDS,
  formatAcres,
  formatCount,
  formatLayerStatsDate,
  formatNumberish,
  formatOutageDisplayDate,
  formatPeople,
  formatRecoveryPercent,
  groupTotal,
  normalizeFloodWidgetFilters,
  occupancyLabel,
  priorityScoreColor,
  severeFemaCount,
  prepareFloodHotspotExemplars,
  stateFilterLabel,
  structureExposureDays,
  structureExposureForState,
  structureExposureSeriesAligned,
  structureExposureTotalsAligned,
  timelineConditionCounts,
  timelineDefaultSortField,
  timelineRows,
  timelineStepExtremes,
  timelineStepLabel,
  topGroupRows,
  windowLabel,
  type CountyPriorityDisplayRow,
  type FemaDepthCrossRow,
  type FloodWidgetFilters,
  type ForecastTimeline,
  type ForecastTimelineFieldName,
  type ForecastTimelineSort,
  type GroupCountRow,
  type LayerQueryLayerResult,
  type LayerRankingSetting,
  type LayerStatsAutomatedPayload,
  type StructureExposureByDayRow,
} from './layerStatsFormat';

function AnalyticsTable({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'tw:min-w-0 tw:w-full tw:overflow-hidden tw:rounded-md tw:[&_[data-slot=table-container]]:overflow-x-hidden tw:[&_[data-slot=table-container]]:bg-transparent',
        className
      )}
    >
      <Table className="tw:table-fixed tw:[&_[data-slot=table-head]]:min-w-0 tw:[&_[data-slot=table-head]]:truncate tw:[&_[data-slot=table-cell]]:min-w-0 tw:[&_[data-slot=table-cell]:not(:has(button))]:truncate">
        {children}
      </Table>
    </div>
  );
}

function KpiTile({
  label,
  value,
  emphasize,
}: {
  label: string;
  value: string;
  emphasize?: boolean;
}) {
  return (
    <div
      className={`tw:rounded-md tw:border tw:px-2.5 tw:py-2 ${
        emphasize
          ? 'tw:border-destructive/40 tw:bg-destructive/15'
          : 'tw:border-border tw:bg-muted/40'
      }`}
    >
      <p className="tw:text-[10px] tw:uppercase tw:tracking-wide tw:text-muted-foreground">
        {label}
      </p>
      <p className="tw:text-sm tw:font-semibold tw:tabular-nums tw:text-foreground">
        {value}
      </p>
    </div>
  );
}

function GroupBars({
  title,
  rows,
  emphasizeValue,
  headerExtra,
}: {
  title: string;
  rows: GroupCountRow[];
  emphasizeValue?: string;
  headerExtra?: React.ReactNode;
}) {
  if (!rows.length && !headerExtra) return null;
  const total = groupTotal(rows) || 1;

  return (
    <div className="tw:space-y-1.5">
      <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-2">
        <p className="tw:text-xs tw:font-medium">{title}</p>
        {headerExtra}
      </div>
      {rows.length === 0 ? (
        <p className="tw:text-[11px] tw:text-muted-foreground">
          No counties for this state.
        </p>
      ) : (
        rows.map((row) => {
          const pct = Math.round(((row.count || 0) / total) * 100);
          const emphasize =
            emphasizeValue != null &&
            row.value.toLowerCase() === emphasizeValue.toLowerCase();
          return (
            <div key={row.value} className="tw:space-y-0.5">
              <div className="tw:flex tw:justify-between tw:gap-2 tw:text-[11px]">
                <span className={emphasize ? 'tw:font-semibold' : ''}>
                  {row.value}
                </span>
                <span className="tw:tabular-nums tw:text-muted-foreground">
                  {formatCount(row.count)} ({pct}%)
                </span>
              </div>
              <div className="tw:h-1.5 tw:w-full tw:rounded-full tw:bg-muted">
                <div
                  className={`tw:h-1.5 tw:rounded-full ${
                    emphasize ? 'tw:bg-destructive' : 'tw:bg-sky-600'
                  }`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

function DemoIncludeMultiselect({
  floodFilters,
  disabled,
  onToggle,
}: {
  floodFilters: FloodWidgetFilters;
  disabled: boolean;
  onToggle: (key: DemoFloodFilterKey, checked: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const selectedCount = DEMO_FLOOD_FILTER_OPTIONS.filter(
    (opt) => floodFilters[opt.key] === true
  ).length;

  return (
    <TooltipProvider>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          render={
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled}
              className="tw:h-7 tw:gap-1 tw:px-2 tw:text-[11px]"
            />
          }
        >
          Include ({selectedCount})
          <ChevronDown className="tw:size-3.5 tw:opacity-70" />
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="tw:w-72 tw:gap-0 tw:p-2"
          sideOffset={6}
        >
          <p className="tw:mb-1.5 tw:px-1 tw:text-[11px] tw:font-medium tw:text-muted-foreground">
            Include data sources
          </p>
          <ul className="tw:m-0 tw:list-none tw:space-y-1 tw:p-0">
            {DEMO_FLOOD_FILTER_OPTIONS.map((opt) => {
              const checked = floodFilters[opt.key] === true;
              return (
                <li
                  key={opt.key}
                  className="tw:flex tw:items-center tw:gap-2 tw:rounded-md tw:px-1 tw:py-1 tw:hover:bg-muted/60"
                >
                  <Checkbox
                    checked={checked}
                    disabled={disabled}
                    onCheckedChange={(value) => {
                      onToggle(opt.key, value === true);
                    }}
                    aria-label={opt.label}
                  />
                  <span className="tw:min-w-0 tw:flex-1 tw:text-[11px] tw:text-foreground">
                    {opt.label}
                  </span>
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <button
                          type="button"
                          className="tw:inline-flex tw:size-5 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:text-muted-foreground tw:hover:bg-muted tw:hover:text-foreground"
                          aria-label={`About ${opt.label}`}
                        >
                          <Info className="tw:size-3.5" />
                        </button>
                      }
                    />
                    <TooltipContent
                      side="right"
                      className="tw:max-w-56 tw:text-xs"
                    >
                      {opt.description}
                    </TooltipContent>
                  </Tooltip>
                </li>
              );
            })}
          </ul>
        </PopoverContent>
      </Popover>
    </TooltipProvider>
  );
}

function PopulationServiceCapacityBars() {
  const rows = populationServiceCapacityRowsSorted();
  const maxCapacity = Math.max(...rows.map((r) => r.capacity), 1);

  return (
    <div className="tw:space-y-1.5">
      <p className="tw:text-xs tw:font-medium">Population Service Capacity</p>
      <p className="tw:text-[10px] tw:text-muted-foreground">
        People that can be served with available staging resources.
      </p>
      {rows.map((row) => {
        const widthPct = Math.max(
          4,
          Math.round((row.capacity / maxCapacity) * 100)
        );
        return (
          <div key={row.county} className="tw:space-y-0.5">
            <div className="tw:flex tw:justify-between tw:gap-2 tw:text-[11px]">
              <span>{row.county}</span>
              <span className="tw:tabular-nums tw:text-muted-foreground">
                {formatPeople(row.capacity)}
              </span>
            </div>
            <div className="tw:h-1.5 tw:w-full tw:overflow-hidden tw:rounded-full tw:bg-muted">
              <div
                className="tw:h-1.5 tw:rounded-full tw:bg-sky-600"
                style={{ width: `${widthPct}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CountyPriorityBars({
  title,
  rows,
}: {
  title: string;
  rows: CountyPriorityDisplayRow[];
}) {
  if (!rows.length) return null;
  const legendPriorities = [
    ...new Set(rows.flatMap((r) => r.byPriority.map((p) => p.priority))),
  ].sort((a, b) => Number(b) - Number(a) || b.localeCompare(a));

  return (
    <div className="tw:space-y-1.5">
      <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-2">
        <p className="tw:text-xs tw:font-medium">{title}</p>
        {legendPriorities.length > 0 && (
          <div className="tw:flex tw:flex-wrap tw:gap-1.5">
            {legendPriorities.map((priority) => (
              <span
                key={priority}
                className="tw:inline-flex tw:items-center tw:gap-1 tw:text-[10px] tw:text-muted-foreground"
              >
                <span
                  className="tw:inline-block tw:h-2 tw:w-2 tw:rounded-sm"
                  style={{ backgroundColor: priorityScoreColor(priority) }}
                />
                P{priority}
              </span>
            ))}
          </div>
        )}
      </div>
      {rows.map((row) => {
        const segments =
          row.byPriority.length > 0
            ? row.byPriority
            : [{ priority: '', count: row.total }];
        return (
          <div key={row.label} className="tw:space-y-0.5">
            <div className="tw:flex tw:justify-between tw:gap-2 tw:text-[11px]">
              <span>{row.label}</span>
              <span className="tw:tabular-nums tw:text-muted-foreground">
                {formatCount(row.total)}
              </span>
            </div>
            <div className="tw:flex tw:h-1.5 tw:w-full tw:overflow-hidden tw:rounded-full tw:bg-muted">
              {segments.map((seg, i) => {
                const widthPct =
                  row.total > 0
                    ? Math.max(0, (seg.count / row.total) * 100)
                    : 0;
                if (widthPct <= 0) return null;
                return (
                  <div
                    key={`${row.label}-${seg.priority || 'total'}-${i}`}
                    className="tw:h-1.5"
                    title={
                      seg.priority
                        ? `Priority ${seg.priority}: ${formatCount(seg.count)}`
                        : formatCount(seg.count)
                    }
                    style={{
                      width: `${widthPct}%`,
                      backgroundColor: seg.priority
                        ? priorityScoreColor(seg.priority)
                        : '#0284c7',
                    }}
                  />
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const FEMA_PIE_COLORS: Record<string, string> = {
  Destroyed: '#dc2626',
  Major: '#ea580c',
  Minor: '#d97706',
  Affected: '#16a34a',
};

const STRUCTURE_STACK_COLORS: Record<string, string> = {
  Residential: '#2563eb',
  Agriculture: '#65a30d',
  Assembly: '#7c3aed',
  Commercial: '#0891b2',
  Education: '#ca8a04',
  Government: '#475569',
  Industrial: '#c2410c',
  Unclassified: '#94a3b8',
  Utility_and_Misc: '#0f766e',
};

/** Chart chrome for Analytics dark island (legend/axis/tooltip). */
const CHART_TEXT = '#cbd5e1';
const CHART_MUTED = '#94a3b8';
const CHART_SPLIT = '#334155';
const CHART_PIE_BORDER = '#1e293b';
const CHART_TOOLTIP = {
  backgroundColor: 'rgba(15, 23, 42, 0.94)',
  borderColor: '#334155',
  textStyle: { color: '#f1f5f9', fontSize: 11 },
};

function forecastPeriodHoverLabel(value: string): string {
  const hours = Number(value.trim());
  if (Number.isInteger(hours) && hours >= 0) {
    return `${hours}-hour forecast period`;
  }
  return value;
}

/** Categorical donut; hover shows label, count, and percent. */
function GroupDonut({
  title,
  rows,
  hoverLabel,
}: {
  title: string;
  rows: GroupCountRow[];
  hoverLabel?: (value: string) => string;
}) {
  const option = useMemo(() => {
    const total = groupTotal(rows) || 1;
    return {
      tooltip: {
        trigger: 'item' as const,
        ...CHART_TOOLTIP,
        formatter: (params: {
          name?: string;
          value?: number;
          percent?: number;
        }) => {
          const raw = params?.name || '';
          const label = hoverLabel ? hoverLabel(raw) : raw;
          const count = typeof params?.value === 'number' ? params.value : 0;
          const pct =
            typeof params?.percent === 'number'
              ? params.percent
              : Math.round((count / total) * 100);
          return `<strong>${label}</strong><br/>${formatCount(count)} (${pct}%)`;
        },
      },
      legend: {
        type: 'scroll' as const,
        bottom: 0,
        textStyle: { fontSize: 10, color: CHART_TEXT },
        pageTextStyle: { color: CHART_MUTED },
      },
      series: [
        {
          type: 'pie' as const,
          radius: ['35%', '62%'],
          center: ['50%', '45%'],
          avoidLabelOverlap: true,
          itemStyle: {
            borderRadius: 3,
            borderColor: CHART_PIE_BORDER,
            borderWidth: 1,
          },
          label: {
            formatter: '{d}%',
            fontSize: 10,
            color: CHART_TEXT,
          },
          data: rows.map((r) => ({
            name: r.value,
            value: r.count,
          })),
        },
      ],
    };
  }, [hoverLabel, rows]);

  if (!rows.length) return null;

  return (
    <div className="tw:rounded-md tw:border tw:px-2 tw:py-2">
      <p className="tw:mb-1 tw:text-xs tw:font-medium">{title}</p>
      <ReactECharts
        option={option}
        style={{ height: 200 }}
        opts={{ renderer: 'svg' }}
      />
    </div>
  );
}

/** Combined FEMA pie; hover shows depth-bin mix from the joint cross-tab. */
function FemaDepthPie({
  cross,
  fallbackFema,
}: {
  cross: FemaDepthCrossRow[] | undefined;
  fallbackFema: GroupCountRow[];
}) {
  const femaRows = useMemo(() => {
    const fromCross = femaTotalsFromCross(cross);
    return fromCross.length > 0 ? fromCross : fallbackFema;
  }, [cross, fallbackFema]);

  const option = useMemo(() => {
    const total = groupTotal(femaRows) || 1;
    return {
      tooltip: {
        trigger: 'item' as const,
        ...CHART_TOOLTIP,
        formatter: (params: {
          name?: string;
          value?: number;
          percent?: number;
        }) => {
          const fema = params?.name || '';
          const count = typeof params?.value === 'number' ? params.value : 0;
          const pct =
            typeof params?.percent === 'number'
              ? params.percent
              : Math.round((count / total) * 100);
          const depths = depthBreakdownForFema(cross, fema);
          const depthTotal = groupTotal(depths) || count || 1;
          const depthLines =
            depths.length > 0
              ? depths
                  .map((d) => {
                    const dp = Math.round(((d.count || 0) / depthTotal) * 100);
                    return `${d.value}: ${formatCount(d.count)} (${dp}%)`;
                  })
                  .join('<br/>')
              : 'No depth breakdown';
          return `<strong>${fema}</strong>: ${formatCount(count)} (${pct}%)<br/><em>Depth</em><br/>${depthLines}`;
        },
      },
      legend: {
        type: 'scroll' as const,
        bottom: 0,
        textStyle: { fontSize: 10, color: CHART_TEXT },
        pageTextStyle: { color: CHART_MUTED },
      },
      series: [
        {
          type: 'pie' as const,
          radius: ['35%', '62%'],
          center: ['50%', '45%'],
          avoidLabelOverlap: true,
          itemStyle: {
            borderRadius: 3,
            borderColor: CHART_PIE_BORDER,
            borderWidth: 1,
          },
          label: {
            formatter: '{d}%',
            fontSize: 10,
            color: CHART_TEXT,
          },
          data: femaRows.map((r) => ({
            name: r.value,
            value: r.count,
            itemStyle: FEMA_PIE_COLORS[r.value]
              ? { color: FEMA_PIE_COLORS[r.value] }
              : undefined,
          })),
        },
      ],
    };
  }, [cross, femaRows]);

  if (!femaRows.length) return null;

  return (
    <div className="tw:rounded-md tw:border tw:px-2 tw:py-2">
      <p className="tw:mb-1 tw:text-xs tw:font-medium">
        FEMA classification
        <span className="tw:ml-1 tw:font-normal tw:text-muted-foreground">
          (hover for depth)
        </span>
      </p>
      <ReactECharts
        option={option}
        style={{ height: 200 }}
        opts={{ renderer: 'svg' }}
      />
    </div>
  );
}

function StructureExposureChart({
  rows,
  state,
}: {
  rows: StructureExposureByDayRow[];
  state: string;
}) {
  const days = structureExposureDays(rows);
  const seriesByField = structureExposureSeriesAligned(rows, days);
  const totals = structureExposureTotalsAligned(rows, days);

  const option = useMemo(
    () => ({
      grid: { left: 44, right: 12, top: 28, bottom: 36 },
      legend: {
        type: 'scroll' as const,
        top: 0,
        textStyle: { fontSize: 9, color: CHART_TEXT },
        pageTextStyle: { color: CHART_MUTED },
      },
      tooltip: {
        trigger: 'axis' as const,
        axisPointer: { type: 'shadow' as const },
        ...CHART_TOOLTIP,
        formatter: (
          params: Array<{ seriesName: string; data: number; axisValue: string }>
        ) => {
          if (!params?.length) return '';
          const day = params[0].axisValue;
          const dayIdx = days.indexOf(day);
          const total = dayIdx >= 0 ? totals[dayIdx] : 0;
          const lines = params
            .filter((p) => (p.data || 0) > 0)
            .map(
              (p) => `${occupancyLabel(p.seriesName)}: ${formatCount(p.data)}`
            );
          return `<strong>${day}</strong><br/>Total structures: ${formatCount(total)}<br/>${lines.join('<br/>')}`;
        },
      },
      xAxis: {
        type: 'category' as const,
        data: days,
        axisLabel: {
          fontSize: 9,
          rotate: days.length > 6 ? 35 : 0,
          color: CHART_MUTED,
        },
        axisLine: { lineStyle: { color: CHART_SPLIT } },
      },
      yAxis: {
        type: 'value' as const,
        axisLabel: { fontSize: 9, color: CHART_MUTED },
        splitLine: {
          lineStyle: { type: 'dashed' as const, color: CHART_SPLIT },
        },
      },
      series: FLOOD_OCCUPANCY_FIELDS.map((field) => ({
        name: field,
        type: 'bar' as const,
        stack: 'structures',
        emphasis: { focus: 'series' as const },
        itemStyle: {
          color: STRUCTURE_STACK_COLORS[field] || undefined,
        },
        data: seriesByField[field] || [],
      })),
    }),
    [days, seriesByField, totals]
  );

  const scopeLabel = stateFilterLabel(state);

  if (!state) {
    return (
      <div className="tw:rounded-md tw:border tw:px-2 tw:py-2">
        <p className="tw:text-xs tw:font-medium">Structures exposed by day</p>
        <p className="tw:text-[11px] tw:text-muted-foreground">
          Select a state to view structure exposure.
        </p>
      </div>
    );
  }

  if (!days.length) {
    return (
      <div className="tw:rounded-md tw:border tw:px-2 tw:py-2">
        <p className="tw:text-xs tw:font-medium">
          Structures exposed by day ({scopeLabel})
        </p>
        <p className="tw:text-[11px] tw:text-muted-foreground">
          {state === ALL_STATES_FILTER
            ? 'No structure exposure in the window.'
            : 'No structure exposure for this state in the window.'}
        </p>
      </div>
    );
  }

  return (
    <div className="tw:rounded-md tw:border tw:px-2 tw:py-2">
      <p className="tw:mb-1 tw:text-xs tw:font-medium">
        Structures exposed by day ({scopeLabel})
      </p>
      <ReactECharts
        option={option}
        style={{ height: 240 }}
        opts={{ renderer: 'svg' }}
      />
    </div>
  );
}

function femaBadgeVariant(
  value: unknown
): 'destructive' | 'orange' | 'amber' | 'green' | 'secondary' {
  const v = String(value || '').toLowerCase();
  if (v === 'destroyed') return 'destructive';
  if (v === 'major') return 'orange';
  if (v === 'minor') return 'amber';
  if (v === 'affected') return 'green';
  return 'secondary';
}

function FloodCard({
  layer,
  floodFilters,
  onFloodFiltersChange,
  filtersDisabled = false,
}: {
  layer: LayerQueryLayerResult;
  floodFilters: FloodWidgetFilters;
  onFloodFiltersChange?: (next: FloodWidgetFilters) => void;
  filtersDisabled?: boolean;
}) {
  const femaFallback = topGroupRows(layer.groupCounts?.fema_classification, 8);
  const states = topGroupRows(layer.groupCounts?.STATE_ABBR, 8);
  const stateOptions = useMemo(() => {
    const fromCounties = Object.keys(layer.countiesByState || {});
    const fromExposure = [
      ...new Set(
        (layer.structureExposureByDay || []).map((r) => r.state).filter(Boolean)
      ),
    ];
    const merged = new Set([
      ...fromCounties,
      ...fromExposure,
      ...states.map((s) => s.value),
    ]);
    const current =
      typeof floodFilters.stateAbbr === 'string' &&
      floodFilters.stateAbbr &&
      floodFilters.stateAbbr !== ALL_STATES_FILTER
        ? floodFilters.stateAbbr
        : '';
    if (current) merged.add(current);
    if (merged.size) return [...merged].sort((a, b) => a.localeCompare(b));
    return states.map((s) => s.value);
  }, [
    layer.countiesByState,
    layer.structureExposureByDay,
    states,
    floodFilters.stateAbbr,
  ]);

  const selectedState =
    floodFilters.stateAbbr && floodFilters.stateAbbr !== ALL_STATES_FILTER
      ? floodFilters.stateAbbr
      : floodFilters.stateAbbr === ALL_STATES_FILTER
        ? ALL_STATES_FILTER
        : defaultCountyState(layer.countiesByState, stateOptions) ||
          ALL_STATES_FILTER;

  const globalCounties = topGroupRows(layer.groupCounts?.COUNTY_NAME, 15);
  const topCountyBreakdown = (() => {
    const fromBreakdown = countyPriorityBreakdownForStateOrAll(
      layer.countyPriorityBreakdown,
      selectedState,
      5,
      {
        countiesByState: layer.countiesByState,
        globalCountyRows: globalCounties,
      }
    );
    let rows: CountyPriorityDisplayRow[] = fromBreakdown;
    if (rows.length === 0 && selectedState !== ALL_STATES_FILTER) {
      rows = topGroupRows(layer.groupCounts?.COUNTY_NAME, 5).map((row) => ({
        label: row.value,
        total: row.count,
        byPriority: [] as Array<{ priority: string; count: number }>,
      }));
    }
    return applyDemoResourceCapacityCountyBoost(
      rows,
      floodFilters.demoResourceCapacity === true
    );
  })();
  const severe = severeFemaCount(layer.groupCounts);
  const exemplars = prepareFloodHotspotExemplars(layer.exemplars, 8);
  const exposureForState = structureExposureForState(
    layer.structureExposureByDay,
    selectedState
  );

  const confidenceValue =
    typeof floodFilters.confidenceOverallMin === 'number' &&
    Number.isFinite(floodFilters.confidenceOverallMin)
      ? String(floodFilters.confidenceOverallMin)
      : '';

  const emitFilters = (patch: Partial<FloodWidgetFilters>) => {
    if (!onFloodFiltersChange) return;
    onFloodFiltersChange(
      normalizeFloodWidgetFilters({ ...floodFilters, ...patch })
    );
  };

  return (
    <Card size="sm" className="tw:h-full">
      <CardHeader className="tw:border-b tw:border-border tw:pb-(--card-spacing)">
        <CardTitle className="tw:text-sm">
          Flood impact
          <span className="tw:ml-2 tw:text-xs tw:font-normal tw:text-muted-foreground">
            {layer.name}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="tw:space-y-3">
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3 tw:rounded-md tw:border tw:border-border tw:bg-muted/40 tw:px-2.5 tw:py-2">
          <p className="tw:text-[11px] tw:font-medium tw:text-muted-foreground">
            Filters
          </p>
          <label className="tw:flex tw:items-center tw:gap-1.5 tw:text-[11px] tw:text-muted-foreground">
            State
            <select
              className="tw:rounded tw:border tw:border-input tw:bg-background tw:px-1.5 tw:py-0.5 tw:text-[11px] tw:text-foreground"
              value={selectedState || ALL_STATES_FILTER}
              disabled={filtersDisabled || !onFloodFiltersChange}
              onChange={(e) => emitFilters({ stateAbbr: e.target.value })}
            >
              <option value={ALL_STATES_FILTER}>All states</option>
              {stateOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <label className="tw:flex tw:items-center tw:gap-1.5 tw:text-[11px] tw:text-muted-foreground">
            Confidence
            <select
              className="tw:rounded tw:border tw:border-input tw:bg-background tw:px-1.5 tw:py-0.5 tw:text-[11px] tw:text-foreground"
              value={confidenceValue}
              disabled={filtersDisabled || !onFloodFiltersChange}
              onChange={(e) => {
                const raw = e.target.value;
                emitFilters({
                  confidenceOverallMin: raw === '' ? null : Number(raw),
                });
              }}
            >
              <option value="">Any</option>
              {CONFIDENCE_OVERALL_MIN_OPTIONS.map((min) => (
                <option key={min} value={String(min)}>
                  ≥ {min}
                </option>
              ))}
            </select>
          </label>
          {LAYER_STATS_DEMO_FILTERS_ENABLED ? (
            <DemoIncludeMultiselect
              floodFilters={floodFilters}
              disabled={filtersDisabled || !onFloodFiltersChange}
              onToggle={(key, checked) => {
                emitFilters({ [key]: checked });
              }}
            />
          ) : null}
        </div>

        <div className="tw:grid tw:grid-cols-2 tw:gap-2">
          <KpiTile label="Flood bins" value={formatCount(layer.totalCount)} />
          <KpiTile
            label="Severe (Destroyed+Major)"
            value={formatCount(severe)}
            emphasize={severe > 0}
          />
          <KpiTile
            label="Population"
            value={formatPeople(layer.stats?.population_sum)}
          />
        </div>

        <FemaDepthPie
          cross={layer.femaDepthCross}
          fallbackFema={femaFallback}
        />

        <StructureExposureChart rows={exposureForState} state={selectedState} />

        {LAYER_STATS_DEMO_FILTERS_ENABLED &&
        floodFilters.demoResourceCapacity === true ? (
          <PopulationServiceCapacityBars />
        ) : null}

        <CountyPriorityBars
          title="Priority Ranked Counties"
          rows={topCountyBreakdown}
        />

        {exemplars.length > 0 && (
          <div>
            <p className="tw:mb-1.5 tw:text-xs tw:font-medium">
              Priority hotspots
            </p>
            <AnalyticsTable>
              <TableHeader>
                <TableRow>
                  <TableHead className="tw:w-10">Map</TableHead>
                  <TableHead>County</TableHead>
                  <TableHead>FEMA</TableHead>
                  <TableHead>Depth</TableHead>
                  <TableHead className="tw:text-right">Priority</TableHead>
                  <TableHead className="tw:text-right">Population</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {exemplars.map((ex, i) => (
                  <TableRow
                    key={`flood-ex-${String(ex.OBJECTID ?? ex.BIN_ID ?? 'row')}-${i}`}
                  >
                    <TableCell>
                      <MapFocusButton
                        layerId={layer.id}
                        exemplar={ex}
                        title={`${ex.COUNTY_NAME || 'Flood bin'} (${ex.fema_classification || '—'})`}
                      />
                    </TableCell>
                    <TableCell className="tw:whitespace-nowrap">
                      {String(ex.COUNTY_NAME || '—')}
                      {ex.STATE_ABBR ? (
                        <span className="tw:text-muted-foreground">
                          {' '}
                          ({String(ex.STATE_ABBR)})
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Badge variant={femaBadgeVariant(ex.fema_classification)}>
                        {String(ex.fema_classification || '—')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {String(ex.depth_bin_ft_range || '—')}
                    </TableCell>
                    <TableCell className="tw:text-right tw:tabular-nums">
                      {formatCount(ex.priority_score as number)}
                    </TableCell>
                    <TableCell className="tw:text-right tw:tabular-nums">
                      {formatPeople(ex.population)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </AnalyticsTable>
          </div>
        )}

        {layer.queryErrors && layer.queryErrors.length > 0 && (
          <div className="tw:text-xs tw:text-destructive">
            <strong>Query errors</strong>
            <ul className="tw:mb-0 tw:mt-1">
              {layer.queryErrors.map((e) => (
                <li key={e.query}>
                  {e.query}: {e.error}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function QueryErrorList({ layer }: { layer: LayerQueryLayerResult }) {
  if (!layer.queryErrors?.length) return null;
  return (
    <div className="tw:text-xs tw:text-destructive">
      <strong>Query errors</strong>
      <ul className="tw:mb-0 tw:mt-1">
        {layer.queryErrors.map((e) => (
          <li key={e.query}>
            {e.query}: {e.error}
          </li>
        ))}
      </ul>
    </div>
  );
}

function severityBadgeVariant(
  value: unknown
): 'destructive' | 'orange' | 'amber' | 'green' | 'secondary' {
  const v = String(value || '').toLowerCase();
  if (v === 'extreme' || v === 'major') return 'destructive';
  if (v === 'severe') return 'orange';
  if (v === 'moderate' || v === 'minor') return 'amber';
  if (v === 'action') return 'green';
  return 'secondary';
}

function attr(row: Record<string, unknown>, keys: string[]): unknown {
  for (const key of keys) {
    if (row[key] != null && row[key] !== '') return row[key];
  }
  return undefined;
}

function PrecipCard({ layer }: { layer: LayerQueryLayerResult }) {
  const aorText = useAorText();
  const top = layer.exemplars?.[0];
  const highestLabel =
    (top?.label as string) ||
    (typeof layer.stats?.category_max === 'number'
      ? `Class ${layer.stats.category_max}`
      : '—');
  const exemplars = (layer.exemplars || []).slice(0, 8);

  return (
    <Card size="sm" className="tw:h-full">
      <CardHeader className="tw:border-b tw:border-border tw:pb-(--card-spacing)">
        <CardTitle className="tw:text-sm">
          Forecast precipitation
          <span className="tw:ml-2 tw:text-xs tw:font-normal tw:text-muted-foreground">
            {layer.name}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="tw:space-y-3">
        <div className="tw:grid tw:grid-cols-2 tw:gap-2">
          <KpiTile
            label={aorText('Polygons in AOR')}
            value={formatCount(layer.totalCount)}
          />
          <KpiTile label="Highest class" value={String(highestLabel)} />
          <KpiTile
            label="Highest-class area"
            value={formatNumberish(layer.stats?.highest_class_area, 0)}
          />
        </div>
        <GroupBars
          title="Rainfall class"
          rows={topGroupRows(layer.groupCounts?.label, 8)}
        />
        {exemplars.length > 0 && (
          <div>
            <p className="tw:mb-1.5 tw:text-xs tw:font-medium">
              Top forecast polygons
            </p>
            <AnalyticsTable>
              <TableHeader>
                <TableRow>
                  <TableHead className="tw:w-10">Map</TableHead>
                  <TableHead>Class</TableHead>
                  <TableHead>Window</TableHead>
                  <TableHead className="tw:text-right">Area</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {exemplars.map((ex, i) => (
                  <TableRow key={`precip-${String(ex.OBJECTID ?? i)}`}>
                    <TableCell>
                      <MapFocusButton
                        layerId={layer.id}
                        exemplar={ex}
                        title={String(ex.label || 'Precipitation')}
                      />
                    </TableCell>
                    <TableCell>{String(ex.label || '—')}</TableCell>
                    <TableCell className="tw:whitespace-nowrap tw:text-xs">
                      {formatLayerStatsDate(ex.fromdate)} –{' '}
                      {formatLayerStatsDate(ex.todate)}
                    </TableCell>
                    <TableCell className="tw:text-right tw:tabular-nums">
                      {formatNumberish(ex.Shape__Area, 0)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </AnalyticsTable>
          </div>
        )}
        <QueryErrorList layer={layer} />
      </CardContent>
    </Card>
  );
}

function NwsCard({ layer }: { layer: LayerQueryLayerResult }) {
  const aorText = useAorText();
  const exemplars = (layer.exemplars || []).slice(0, 8);
  return (
    <Card size="sm" className="tw:h-full">
      <CardHeader className="tw:border-b tw:border-border tw:pb-(--card-spacing)">
        <CardTitle className="tw:text-sm">{layer.name}</CardTitle>
      </CardHeader>
      <CardContent className="tw:space-y-3">
        <div className="tw:grid tw:grid-cols-2 tw:gap-2">
          <KpiTile
            label={aorText('Events in AOR')}
            value={formatCount(layer.totalCount)}
          />
          <KpiTile
            label="Extreme + Severe"
            value={formatCount(layer.stats?.extreme_severe_count)}
            emphasize={(layer.stats?.extreme_severe_count ?? 0) > 0}
          />
          <KpiTile
            label="Expiring in 24h"
            value={formatCount(layer.stats?.expiring_24h_count)}
            emphasize={(layer.stats?.expiring_24h_count ?? 0) > 0}
          />
        </div>
        <GroupBars
          title="Severity"
          rows={topGroupRows(layer.groupCounts?.Severity, 8)}
        />
        <GroupBars
          title="Event"
          rows={topGroupRows(layer.groupCounts?.Event, 8)}
        />
        {exemplars.length > 0 && (
          <div>
            <p className="tw:mb-1.5 tw:text-xs tw:font-medium">Top events</p>
            <AnalyticsTable>
              <TableHeader>
                <TableRow>
                  <TableHead className="tw:w-10">Map</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead className="tw:w-[5.75rem]">Severity</TableHead>
                  <TableHead>Affected</TableHead>
                  <TableHead className="tw:w-16">End</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {exemplars.map((ex, i) => (
                  <TableRow key={`nws-${String(ex.OBJECTID ?? i)}`}>
                    <TableCell>
                      <MapFocusButton
                        layerId={layer.id}
                        exemplar={ex}
                        title={String(ex.Event || 'NWS event')}
                      />
                    </TableCell>
                    <TableCell>{String(ex.Event || '—')}</TableCell>
                    <TableCell>
                      <Badge variant={severityBadgeVariant(ex.Severity)}>
                        {String(ex.Severity || '—')}
                      </Badge>
                    </TableCell>
                    <TableCell
                      className="tw:text-xs"
                      title={String(ex.Affected || '—')}
                    >
                      {String(ex.Affected || '—')}
                    </TableCell>
                    <TableCell className="tw:whitespace-nowrap tw:text-xs">
                      {formatLayerStatsDate(ex.End_)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </AnalyticsTable>
          </div>
        )}
        <QueryErrorList layer={layer} />
      </CardContent>
    </Card>
  );
}

function HurricaneConeCard({ layer }: { layer: LayerQueryLayerResult }) {
  const exemplars = (layer.exemplars || []).slice(0, 8);
  const stormCount =
    typeof layer.stats?.storm_count === 'number'
      ? layer.stats.storm_count
      : (layer.groupCounts?.STORMNAME || []).length;
  return (
    <Card size="sm" className="tw:h-full">
      <CardHeader className="tw:border-b tw:border-border tw:pb-(--card-spacing)">
        <CardTitle className="tw:text-sm">Hurricane forecast cone</CardTitle>
      </CardHeader>
      <CardContent className="tw:space-y-3">
        <div className="tw:grid tw:grid-cols-2 tw:gap-2">
          <KpiTile label="Storms" value={formatCount(stormCount)} />
          <KpiTile
            label="Max wind"
            value={formatNumberish(layer.stats?.max_wind, 0)}
          />
          <KpiTile
            label="Max gust"
            value={formatNumberish(layer.stats?.max_gust, 0)}
          />
          <KpiTile
            label="Max surge"
            value={formatNumberish(layer.stats?.max_surge, 1)}
          />
        </div>
        <div className="tw:grid tw:grid-cols-2 tw:gap-2">
          <GroupDonut
            title="Storm type"
            rows={topGroupRows(layer.groupCounts?.STORMTYPE, 8)}
          />
          <GroupDonut
            title="Forecast period"
            rows={topGroupRows(layer.groupCounts?.FCSTPRD, 8)}
            hoverLabel={forecastPeriodHoverLabel}
          />
        </div>
        {exemplars.length > 0 && (
          <div>
            <p className="tw:mb-1.5 tw:text-xs tw:font-medium">
              Latest advisory per storm
            </p>
            <AnalyticsTable>
              <TableHeader>
                <TableRow>
                  <TableHead className="tw:w-10">Map</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Advisory</TableHead>
                  <TableHead className="tw:text-right">Wind</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {exemplars.map((ex, i) => (
                  <TableRow key={`cone-${String(ex.OBJECTID ?? i)}`}>
                    <TableCell>
                      <MapFocusButton
                        layerId={layer.id}
                        exemplar={ex}
                        title={String(ex.STORMNAME || 'Storm')}
                        mapLayerTitle="Forecast Error Cone and Danger Area"
                      />
                    </TableCell>
                    <TableCell>{String(ex.STORMNAME || '—')}</TableCell>
                    <TableCell>{String(ex.STORMTYPE || '—')}</TableCell>
                    <TableCell className="tw:text-xs">
                      {String(ex.ADVISNUM || '—')}
                    </TableCell>
                    <TableCell className="tw:text-right tw:tabular-nums">
                      {formatNumberish(ex.MAX_WIND, 0)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </AnalyticsTable>
          </div>
        )}
        <QueryErrorList layer={layer} />
      </CardContent>
    </Card>
  );
}

function HurricaneForecastCard({ layer }: { layer: LayerQueryLayerResult }) {
  const exemplars = (layer.exemplars || []).slice(0, 8);
  return (
    <Card size="sm" className="tw:h-full">
      <CardHeader className="tw:border-b tw:border-border tw:pb-(--card-spacing)">
        <CardTitle className="tw:text-sm">
          Hurricane forecast position
        </CardTitle>
      </CardHeader>
      <CardContent className="tw:space-y-3">
        <div className="tw:grid tw:grid-cols-2 tw:gap-2">
          <KpiTile
            label="Forecast points"
            value={formatCount(layer.totalCount)}
          />
          <KpiTile
            label="Max wind"
            value={formatNumberish(layer.stats?.max_wind, 0)}
          />
          <KpiTile
            label="Max gust"
            value={formatNumberish(layer.stats?.max_gust, 0)}
          />
        </div>
        <GroupDonut
          title="Development / type"
          rows={topGroupRows(
            layer.groupCounts?.TCDVLP || layer.groupCounts?.STORMTYPE,
            8
          )}
        />
        {exemplars.length > 0 && (
          <div>
            <p className="tw:mb-1.5 tw:text-xs tw:font-medium">
              Next forecast points
            </p>
            <AnalyticsTable>
              <TableHeader>
                <TableRow>
                  <TableHead className="tw:w-10">Map</TableHead>
                  <TableHead>Storm</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="tw:text-right">Wind</TableHead>
                  <TableHead className="tw:text-right">Gust</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {exemplars.map((ex, i) => (
                  <TableRow key={`fcst-${String(ex.OBJECTID ?? i)}`}>
                    <TableCell>
                      <MapFocusButton
                        layerId={layer.id}
                        exemplar={ex}
                        title={String(ex.STORMNAME || 'Forecast point')}
                        mapLayerTitle="Forecast Position"
                      />
                    </TableCell>
                    <TableCell>{String(ex.STORMNAME || '—')}</TableCell>
                    <TableCell className="tw:whitespace-nowrap tw:text-xs">
                      {String(attr(ex, ['DATELBL', 'FLDATELBL']) || '—')}
                    </TableCell>
                    <TableCell className="tw:text-right tw:tabular-nums">
                      {formatNumberish(attr(ex, ['MAXWIND', 'MAX_WIND']), 0)}
                    </TableCell>
                    <TableCell className="tw:text-right tw:tabular-nums">
                      {formatNumberish(attr(ex, ['GUST', 'MAX_GUST']), 0)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </AnalyticsTable>
          </div>
        )}
        <QueryErrorList layer={layer} />
      </CardContent>
    </Card>
  );
}

function HurricaneObservedCard({ layer }: { layer: LayerQueryLayerResult }) {
  const exemplars = (layer.exemplars || []).slice(0, 8);
  return (
    <Card size="sm" className="tw:h-full">
      <CardHeader className="tw:border-b tw:border-border tw:pb-(--card-spacing)">
        <CardTitle className="tw:text-sm">
          Hurricane observed position
        </CardTitle>
      </CardHeader>
      <CardContent className="tw:space-y-3">
        <div className="tw:grid tw:grid-cols-2 tw:gap-2">
          <KpiTile
            label="Observed points"
            value={formatCount(layer.totalCount)}
          />
          <KpiTile
            label="Latest max wind"
            value={formatNumberish(layer.stats?.max_wind, 0)}
          />
        </div>
        {exemplars.length > 0 && (
          <div>
            <p className="tw:mb-1.5 tw:text-xs tw:font-medium">
              Most recent point per storm
            </p>
            <AnalyticsTable>
              <TableHeader>
                <TableRow>
                  <TableHead className="tw:w-10">Map</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Datetime</TableHead>
                  <TableHead className="tw:text-right">Wind</TableHead>
                  <TableHead className="tw:text-right">SS</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {exemplars.map((ex, i) => (
                  <TableRow key={`obs-${String(ex.OBJECTID ?? i)}`}>
                    <TableCell>
                      <MapFocusButton
                        layerId={layer.id}
                        exemplar={ex}
                        title={String(ex.STORMNAME || 'Observed point')}
                        mapLayerTitle="Observed Position"
                      />
                    </TableCell>
                    <TableCell>{String(ex.STORMNAME || '—')}</TableCell>
                    <TableCell className="tw:whitespace-nowrap tw:text-xs">
                      {formatLayerStatsDate(ex.DTG)}
                    </TableCell>
                    <TableCell className="tw:text-right tw:tabular-nums">
                      {formatNumberish(
                        attr(ex, ['INTENSITY', 'MAXWIND', 'MAX_WIND']),
                        0
                      )}
                    </TableCell>
                    <TableCell className="tw:text-right tw:tabular-nums">
                      {formatNumberish(attr(ex, ['SS', 'GUST', 'MAX_GUST']), 0)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </AnalyticsTable>
          </div>
        )}
        <QueryErrorList layer={layer} />
      </CardContent>
    </Card>
  );
}

function StreamGaugesCard({ layer }: { layer: LayerQueryLayerResult }) {
  const aorText = useAorText();
  const exemplars = (layer.exemplars || []).slice(0, 8);
  return (
    <Card size="sm" className="tw:h-full">
      <CardHeader className="tw:border-b tw:border-border tw:pb-(--card-spacing)">
        <CardTitle className="tw:text-sm">
          Live stream gauges
          <span className="tw:ml-2 tw:text-xs tw:font-normal tw:text-muted-foreground">
            {layer.name}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="tw:space-y-3">
        <div className="tw:grid tw:grid-cols-2 tw:gap-2">
          <KpiTile
            label={aorText('Gauges in AOR')}
            value={formatCount(layer.totalCount)}
          />
          <KpiTile
            label="Major + Moderate"
            value={formatCount(layer.stats?.major_moderate_count)}
            emphasize={(layer.stats?.major_moderate_count ?? 0) > 0}
          />
          <KpiTile
            label="Action or higher"
            value={formatCount(layer.stats?.action_or_higher_count)}
            emphasize={(layer.stats?.action_or_higher_count ?? 0) > 0}
          />
        </div>
        <GroupBars
          title="Status"
          rows={topGroupRows(layer.groupCounts?.status, 8)}
        />
        {exemplars.length > 0 && (
          <div>
            <p className="tw:mb-1.5 tw:text-xs tw:font-medium">
              Highest status gauges
            </p>
            <AnalyticsTable>
              <TableHeader>
                <TableRow>
                  <TableHead className="tw:w-10">Map</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="tw:text-right">Stage ft</TableHead>
                  <TableHead className="tw:text-right">Flow cfs</TableHead>
                  <TableHead>24h</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {exemplars.map((ex, i) => (
                  <TableRow key={`gauge-${String(ex.OBJECTID ?? i)}`}>
                    <TableCell>
                      <MapFocusButton
                        layerId={layer.id}
                        exemplar={ex}
                        title={String(ex.name || 'Gauge')}
                      />
                    </TableCell>
                    <TableCell>{String(ex.name || '—')}</TableCell>
                    <TableCell>
                      <Badge variant={severityBadgeVariant(ex.status)}>
                        {String(ex.status || '—')}
                      </Badge>
                    </TableCell>
                    <TableCell className="tw:text-right tw:tabular-nums">
                      {formatNumberish(ex.stage_ft, 1)}
                    </TableCell>
                    <TableCell className="tw:text-right tw:tabular-nums">
                      {formatNumberish(ex.flow_cfs, 0)}
                    </TableCell>
                    <TableCell className="tw:text-xs">
                      {String(ex.status_24h || '—')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </AnalyticsTable>
          </div>
        )}
        <QueryErrorList layer={layer} />
      </CardContent>
    </Card>
  );
}

function WildfirePerimetersCard({ layer }: { layer: LayerQueryLayerResult }) {
  const exemplars = (layer.exemplars || []).slice(0, 8);
  return (
    <Card size="sm" className="tw:h-full">
      <CardHeader className="tw:border-b tw:border-border tw:pb-(--card-spacing)">
        <CardTitle className="tw:text-sm">
          Wildfire perimeters
          <span className="tw:ml-2 tw:text-xs tw:font-normal tw:text-muted-foreground">
            {layer.name}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="tw:space-y-3">
        <div className="tw:grid tw:grid-cols-2 tw:gap-2">
          <KpiTile label="Perimeters" value={formatCount(layer.totalCount)} />
          <KpiTile
            label="Total acres"
            value={formatAcres(layer.stats?.acres_sum)}
          />
        </div>
        <GroupBars
          title={
            layer.groupCounts?.FeatureCategory ? 'Feature category' : 'GACC'
          }
          rows={topGroupRows(
            layer.groupCounts?.FeatureCategory || layer.groupCounts?.GACC,
            8
          )}
        />
        {exemplars.length > 0 && (
          <div>
            <p className="tw:mb-1.5 tw:text-xs tw:font-medium">
              Largest perimeters
            </p>
            <AnalyticsTable>
              <TableHeader>
                <TableRow>
                  <TableHead className="tw:w-10">Map</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead className="tw:text-right">Acres</TableHead>
                  <TableHead>Category</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {exemplars.map((ex, i) => (
                  <TableRow key={`wfp-${String(ex.OBJECTID ?? i)}`}>
                    <TableCell>
                      <MapFocusButton
                        layerId={layer.id}
                        exemplar={ex}
                        title={String(ex.IncidentName || 'Perimeter')}
                      />
                    </TableCell>
                    <TableCell>{String(ex.IncidentName || '—')}</TableCell>
                    <TableCell className="tw:text-right tw:tabular-nums">
                      {formatAcres(ex.GISAcres)}
                    </TableCell>
                    <TableCell className="tw:text-xs">
                      {String(ex.FeatureCategory || '—')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </AnalyticsTable>
          </div>
        )}
        <QueryErrorList layer={layer} />
      </CardContent>
    </Card>
  );
}

function WildfireIncidentsCard({ layer }: { layer: LayerQueryLayerResult }) {
  const exemplars = (layer.exemplars || []).slice(0, 8);
  const barRows =
    layer.groupCounts?.FireCause || layer.groupCounts?.POOState || [];
  const barTitle = layer.groupCounts?.FireCause ? 'Cause' : 'State';
  return (
    <Card size="sm" className="tw:h-full">
      <CardHeader className="tw:border-b tw:border-border tw:pb-(--card-spacing)">
        <CardTitle className="tw:text-sm">{layer.name}</CardTitle>
      </CardHeader>
      <CardContent className="tw:space-y-3">
        <div className="tw:grid tw:grid-cols-2 tw:gap-2">
          <KpiTile label="Incidents" value={formatCount(layer.totalCount)} />
          <KpiTile
            label="Daily acres"
            value={formatAcres(layer.stats?.acres_sum)}
          />
        </div>
        <GroupBars title={barTitle} rows={topGroupRows(barRows, 8)} />
        {exemplars.length > 0 && (
          <div>
            <p className="tw:mb-1.5 tw:text-xs tw:font-medium">
              Largest incidents
            </p>
            <AnalyticsTable>
              <TableHeader>
                <TableRow>
                  <TableHead className="tw:w-10">Map</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead className="tw:text-right">Acres</TableHead>
                  <TableHead className="tw:text-right">Contained</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {exemplars.map((ex, i) => (
                  <TableRow key={`wfi-${String(ex.OBJECTID ?? i)}`}>
                    <TableCell>
                      <MapFocusButton
                        layerId={layer.id}
                        exemplar={ex}
                        title={String(ex.IncidentName || 'Incident')}
                      />
                    </TableCell>
                    <TableCell>{String(ex.IncidentName || '—')}</TableCell>
                    <TableCell>{String(ex.POOState || '—')}</TableCell>
                    <TableCell className="tw:text-right tw:tabular-nums">
                      {formatAcres(ex.DailyAcres)}
                    </TableCell>
                    <TableCell className="tw:text-right tw:tabular-nums">
                      {typeof ex.PercentContained === 'number'
                        ? `${Math.round(ex.PercentContained)}%`
                        : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </AnalyticsTable>
          </div>
        )}
        <QueryErrorList layer={layer} />
      </CardContent>
    </Card>
  );
}

function PackNotes({ layer }: { layer: LayerQueryLayerResult }) {
  const notes = (layer.notes || []).filter(
    (n) => n !== 'stats_pack_failed_sample_fallback'
  );
  if (!notes.length) return null;
  return (
    <>
      {notes.map((n) => (
        <p key={n} className="tw:text-[11px] tw:text-muted-foreground">
          {n}
        </p>
      ))}
    </>
  );
}

function PackCardShell({
  layer,
  children,
}: {
  layer: LayerQueryLayerResult;
  children: React.ReactNode;
}) {
  return (
    <Card size="sm" className="tw:h-full">
      <CardHeader className="tw:border-b tw:border-border tw:pb-(--card-spacing)">
        <CardTitle className="tw:text-sm">{layer.name}</CardTitle>
      </CardHeader>
      <CardContent className="tw:space-y-3">
        {children}
        <PackNotes layer={layer} />
        <QueryErrorList layer={layer} />
      </CardContent>
    </Card>
  );
}

function EarthquakesCard({ layer }: { layer: LayerQueryLayerResult }) {
  const exemplars = (layer.exemplars || []).slice(0, 8);
  const tsunami = layer.stats?.tsunami_count;
  return (
    <PackCardShell layer={layer}>
      <div className="tw:grid tw:grid-cols-3 tw:gap-2">
        <KpiTile label="Earthquakes" value={formatCount(layer.totalCount)} />
        <KpiTile
          label="Largest"
          value={
            typeof layer.stats?.mag_max === 'number'
              ? `M${formatNumberish(layer.stats.mag_max)}`
              : '—'
          }
          emphasize={(layer.stats?.mag_max ?? 0) >= 6}
        />
        <KpiTile
          label="M5+ / tsunami"
          value={`${formatCount(layer.stats?.m5_plus_count)} / ${formatCount(tsunami)}`}
          emphasize={typeof tsunami === 'number' && tsunami > 0}
        />
      </div>
      <GroupBars
        title="PAGER alert"
        rows={topGroupRows(layer.groupCounts?.alert, 4)}
        emphasizeValue="red"
      />
      {exemplars.length > 0 && (
        <AnalyticsTable>
          <TableHeader>
            <TableRow>
              <TableHead className="tw:w-10">Map</TableHead>
              <TableHead className="tw:w-14 tw:text-right">Mag</TableHead>
              <TableHead>Place</TableHead>
              <TableHead>Time (UTC)</TableHead>
              <TableHead className="tw:w-16">Alert</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {exemplars.map((ex, i) => (
              <TableRow key={`eq-${String(ex.id ?? ex.OBJECTID ?? i)}`}>
                <TableCell>
                  <MapFocusButton
                    layerId={layer.id}
                    exemplar={ex}
                    title={String(ex.place || 'Earthquake')}
                  />
                </TableCell>
                <TableCell className="tw:text-right tw:tabular-nums">
                  {formatNumberish(ex.mag)}
                </TableCell>
                <TableCell>{String(ex.place || '—')}</TableCell>
                <TableCell className="tw:tabular-nums">
                  {formatLayerStatsDate(ex.eventTime)}
                </TableCell>
                <TableCell>{String(ex.alert || '—')}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </AnalyticsTable>
      )}
    </PackCardShell>
  );
}

function ShakeIntensityCard({ layer }: { layer: LayerQueryLayerResult }) {
  const exemplars = (layer.exemplars || []).slice(0, 8);
  return (
    <PackCardShell layer={layer}>
      <div className="tw:grid tw:grid-cols-3 tw:gap-2">
        <KpiTile
          label="Highest MMI"
          value={formatNumberish(layer.stats?.mmi_max)}
          emphasize={(layer.stats?.mmi_max ?? 0) >= 6}
        />
        <KpiTile
          label="MMI 6+ areas"
          value={formatCount(layer.stats?.mmi6_plus_count)}
        />
        <KpiTile
          label="Quake magnitude"
          value={formatNumberish(layer.stats?.mag_max)}
        />
      </div>
      {exemplars.length > 0 && (
        <AnalyticsTable>
          <TableHeader>
            <TableRow>
              <TableHead>Event</TableHead>
              <TableHead className="tw:text-right">MMI</TableHead>
              <TableHead className="tw:text-right">Mag</TableHead>
              <TableHead>Time (UTC)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {exemplars.map((ex, i) => (
              <TableRow
                key={`shake-${String(ex.id ?? i)}-${String(ex.grid_value)}`}
              >
                <TableCell>{String(ex.id || '—')}</TableCell>
                <TableCell className="tw:text-right tw:tabular-nums">
                  {formatNumberish(ex.grid_value)}
                </TableCell>
                <TableCell className="tw:text-right tw:tabular-nums">
                  {formatNumberish(ex.mag)}
                </TableCell>
                <TableCell className="tw:tabular-nums">
                  {formatLayerStatsDate(ex.eventTime)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </AnalyticsTable>
      )}
    </PackCardShell>
  );
}

function ThermalHotspotsCard({ layer }: { layer: LayerQueryLayerResult }) {
  const exemplars = (layer.exemplars || []).slice(0, 8);
  return (
    <PackCardShell layer={layer}>
      <div className="tw:grid tw:grid-cols-3 tw:gap-2">
        <KpiTile label="Detections" value={formatCount(layer.totalCount)} />
        <KpiTile
          label="High confidence"
          value={formatCount(layer.stats?.high_confidence_count)}
          emphasize={(layer.stats?.high_confidence_count ?? 0) > 0}
        />
        <KpiTile
          label="Max fire power"
          value={
            typeof layer.stats?.frp_max === 'number'
              ? `${formatNumberish(layer.stats.frp_max)} MW`
              : '—'
          }
        />
      </div>
      <GroupBars
        title="Confidence"
        rows={topGroupRows(layer.groupCounts?.confidence, 3)}
        emphasizeValue="high"
      />
      {exemplars.length > 0 && (
        <AnalyticsTable>
          <TableHeader>
            <TableRow>
              <TableHead className="tw:w-10">Map</TableHead>
              <TableHead>Location</TableHead>
              <TableHead className="tw:text-right">FRP (MW)</TableHead>
              <TableHead>Confidence</TableHead>
              <TableHead>Detected (UTC)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {exemplars.map((ex, i) => (
              <TableRow key={`viirs-${String(ex.OBJECTID ?? i)}`}>
                <TableCell>
                  <MapFocusButton
                    layerId={layer.id}
                    exemplar={ex}
                    title="Fire detection"
                  />
                </TableCell>
                <TableCell className="tw:tabular-nums">
                  {formatNumberish(ex.latitude, 3)},{' '}
                  {formatNumberish(ex.longitude, 3)}
                </TableCell>
                <TableCell className="tw:text-right tw:tabular-nums">
                  {formatNumberish(ex.frp)}
                </TableCell>
                <TableCell>{String(ex.confidence || '—')}</TableCell>
                <TableCell className="tw:tabular-nums">
                  {formatLayerStatsDate(ex.acq_date)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </AnalyticsTable>
      )}
    </PackCardShell>
  );
}

function WindGustCard({ layer }: { layer: LayerQueryLayerResult }) {
  const exemplars = (layer.exemplars || []).slice(0, 8);
  return (
    <PackCardShell layer={layer}>
      <div className="tw:grid tw:grid-cols-2 tw:gap-2">
        <KpiTile
          label="Strongest gust class"
          value={formatNumberish(layer.stats?.force_max, 0)}
        />
        <KpiTile
          label="Earliest period"
          value={formatLayerStatsDate(layer.stats?.fromdate_min)}
        />
      </div>
      <GroupBars
        title="Gust class"
        rows={topGroupRows(layer.groupCounts?.label, 8)}
      />
      {exemplars.length > 0 && (
        <AnalyticsTable>
          <TableHeader>
            <TableRow>
              <TableHead>Gust class</TableHead>
              <TableHead>From (UTC)</TableHead>
              <TableHead>To (UTC)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {exemplars.map((ex, i) => (
              <TableRow key={`gust-${String(ex.OBJECTID ?? i)}`}>
                <TableCell>{String(ex.label || '—')}</TableCell>
                <TableCell className="tw:tabular-nums">
                  {formatLayerStatsDate(ex.fromdate)}
                </TableCell>
                <TableCell className="tw:tabular-nums">
                  {formatLayerStatsDate(ex.todate)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </AnalyticsTable>
      )}
    </PackCardShell>
  );
}

function HurricaneWindProbabilityCard({
  layer,
}: {
  layer: LayerQueryLayerResult;
}) {
  const max = layer.stats?.pwind_max;
  return (
    <PackCardShell layer={layer}>
      <div className="tw:grid tw:grid-cols-2 tw:gap-2">
        <KpiTile
          label="Highest probability"
          value={typeof max === 'number' ? `${formatNumberish(max, 0)}%` : '—'}
          emphasize={(max ?? 0) >= 50}
        />
        <KpiTile label="Bands in area" value={formatCount(layer.totalCount)} />
      </div>
      <p className="tw:text-[11px] tw:text-muted-foreground">
        NHC probability of these winds at some point in the next 120 hours.
      </p>
    </PackCardShell>
  );
}

function AirQualityCard({ layer }: { layer: LayerQueryLayerResult }) {
  const exemplars = (layer.exemplars || []).slice(0, 8);
  return (
    <PackCardShell layer={layer}>
      <div className="tw:grid tw:grid-cols-3 tw:gap-2">
        <KpiTile label="Monitors" value={formatCount(layer.totalCount)} />
        <KpiTile
          label="Worst PM AQI"
          value={formatCount(layer.stats?.pm_aqi_max)}
          emphasize={(layer.stats?.pm_aqi_max ?? 0) > 150}
        />
        <KpiTile
          label="Worst ozone AQI"
          value={formatCount(layer.stats?.ozone_aqi_max)}
          emphasize={(layer.stats?.ozone_aqi_max ?? 0) > 150}
        />
      </div>
      <GroupBars
        title="PM AQI category"
        rows={topGroupRows(layer.groupCounts?.PM_AQI_LABEL, 6)}
      />
      {exemplars.length > 0 && (
        <AnalyticsTable>
          <TableHeader>
            <TableRow>
              <TableHead className="tw:w-10">Map</TableHead>
              <TableHead>Site</TableHead>
              <TableHead className="tw:text-right">PM AQI</TableHead>
              <TableHead className="tw:text-right">Ozone AQI</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {exemplars.map((ex, i) => (
              <TableRow key={`aq-${String(ex.SiteName ?? i)}-${i}`}>
                <TableCell>
                  <MapFocusButton
                    layerId={layer.id}
                    exemplar={ex}
                    title={String(ex.SiteName || 'Monitor')}
                  />
                </TableCell>
                <TableCell>
                  {String(ex.SiteName || '—')}
                  {ex.StateName ? `, ${String(ex.StateName)}` : ''}
                </TableCell>
                <TableCell className="tw:text-right tw:tabular-nums">
                  {formatCount(ex.PM_AQI)}
                </TableCell>
                <TableCell className="tw:text-right tw:tabular-nums">
                  {formatCount(ex.OZONE_AQI)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </AnalyticsTable>
      )}
    </PackCardShell>
  );
}

function wildBirdResult(ex: Record<string, unknown>): string {
  if (ex.Final_Pathogenicity === 'High Path AI') return 'High-path AI';
  if (ex.Final_H5 === 'Detected') return 'H5';
  if (ex.Final_H7 === 'Detected') return 'H7';
  if (ex.Final_IAV === 'Detected') return 'Influenza A';
  return 'Not detected';
}

function WildBirdAvianInfluenzaCard({
  layer,
}: {
  layer: LayerQueryLayerResult;
}) {
  const exemplars = (layer.exemplars || []).slice(0, 8);
  const hpai = layer.stats?.hpai_detected_count;
  return (
    <PackCardShell layer={layer}>
      <div className="tw:grid tw:grid-cols-2 tw:gap-2 tw:sm:grid-cols-4">
        <KpiTile label="Samples" value={formatCount(layer.totalCount)} />
        <KpiTile
          label="Influenza A"
          value={formatCount(layer.stats?.iav_detected_count)}
        />
        <KpiTile
          label="H5"
          value={formatCount(layer.stats?.h5_detected_count)}
          emphasize={(layer.stats?.h5_detected_count ?? 0) > 0}
        />
        <KpiTile
          label="High-path AI"
          value={formatCount(hpai)}
          emphasize={typeof hpai === 'number' && hpai > 0}
        />
      </div>
      <GroupBars
        title="Influenza A detections by state"
        rows={topGroupRows(layer.groupCounts?.iav_detections_by_state, 6)}
      />
      <GroupBars
        title="Influenza A result"
        rows={topGroupRows(layer.groupCounts?.Final_IAV, 3)}
        emphasizeValue="Detected"
      />
      {exemplars.length > 0 && (
        <AnalyticsTable>
          <TableHeader>
            <TableRow>
              <TableHead className="tw:w-10">Map</TableHead>
              <TableHead>County</TableHead>
              <TableHead>Result</TableHead>
              <TableHead>Collected</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {exemplars.map((ex, i) => {
              const place = [ex.County, ex.State]
                .filter((part) => part != null && part !== '')
                .map(String)
                .join(', ');
              return (
                <TableRow key={`wb-${String(ex.OBJECTID ?? i)}`}>
                  <TableCell>
                    <MapFocusButton
                      layerId={layer.id}
                      exemplar={ex}
                      title={place || 'Wild bird sample'}
                    />
                  </TableCell>
                  <TableCell>{place || '—'}</TableCell>
                  <TableCell>{wildBirdResult(ex)}</TableCell>
                  <TableCell className="tw:tabular-nums">
                    {formatLayerStatsDate(ex.Date_Collected)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </AnalyticsTable>
      )}
    </PackCardShell>
  );
}

const WSSI_IMPACT_LABELS = [
  'Winter weather area',
  'Minor',
  'Moderate',
  'Major',
  'Extreme',
];

function MetarCard({ layer }: { layer: LayerQueryLayerResult }) {
  const exemplars = (layer.exemplars || []).slice(0, 8);
  return (
    <PackCardShell layer={layer}>
      <div className="tw:grid tw:grid-cols-2 tw:gap-2 tw:sm:grid-cols-4">
        <KpiTile label="Stations" value={formatCount(layer.totalCount)} />
        <KpiTile
          label="Strongest gust"
          value={
            typeof layer.stats?.gust_max === 'number'
              ? `${formatNumberish(layer.stats.gust_max, 0)} kt`
              : '—'
          }
        />
        <KpiTile
          label="Warmest"
          value={
            typeof layer.stats?.temp_max === 'number'
              ? `${formatNumberish(layer.stats.temp_max, 0)} F`
              : '—'
          }
        />
        <KpiTile
          label="Coldest"
          value={
            typeof layer.stats?.temp_min === 'number'
              ? `${formatNumberish(layer.stats.temp_min, 0)} F`
              : '—'
          }
        />
      </div>
      {exemplars.length > 0 && (
        <AnalyticsTable>
          <TableHeader>
            <TableRow>
              <TableHead className="tw:w-10">Map</TableHead>
              <TableHead>Station</TableHead>
              <TableHead className="tw:text-right">Gust</TableHead>
              <TableHead className="tw:text-right">Temp</TableHead>
              <TableHead>Weather</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {exemplars.map((ex, i) => (
              <TableRow key={`metar-${String(ex.ICAO ?? ex.OBJECTID ?? i)}`}>
                <TableCell>
                  <MapFocusButton
                    layerId={layer.id}
                    exemplar={ex}
                    title={String(ex.STATION_NAME || ex.ICAO || 'Station')}
                  />
                </TableCell>
                <TableCell>
                  {String(ex.STATION_NAME || ex.ICAO || '—')}
                </TableCell>
                <TableCell className="tw:text-right tw:tabular-nums">
                  {formatNumberish(ex.WIND_GUST, 0)}
                </TableCell>
                <TableCell className="tw:text-right tw:tabular-nums">
                  {formatNumberish(ex.TEMP, 0)}
                </TableCell>
                <TableCell>{String(ex.WEATHER || '—')}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </AnalyticsTable>
      )}
    </PackCardShell>
  );
}

function StormReportsCard({ layer }: { layer: LayerQueryLayerResult }) {
  const exemplars = (layer.exemplars || []).slice(0, 8);
  const fatalities = layer.stats?.fatalities_sum;
  return (
    <PackCardShell layer={layer}>
      <div className="tw:grid tw:grid-cols-3 tw:gap-2">
        <KpiTile label="Reports" value={formatCount(layer.totalCount)} />
        <KpiTile
          label="Largest"
          value={formatNumberish(layer.stats?.magnitude_max)}
        />
        <KpiTile
          label="Fatalities"
          value={formatCount(fatalities)}
          emphasize={typeof fatalities === 'number' && fatalities > 0}
        />
      </div>
      <GroupBars
        title="Report type"
        rows={topGroupRows(layer.groupCounts?.INCIDENT_TYPE, 4)}
      />
      {exemplars.length > 0 && (
        <AnalyticsTable>
          <TableHeader>
            <TableRow>
              <TableHead className="tw:w-10">Map</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Type</TableHead>
              <TableHead className="tw:text-right">Magnitude</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {exemplars.map((ex, i) => {
              const place = [ex.LOCATION, ex.STATE]
                .filter((part) => part != null && part !== '')
                .map(String)
                .join(', ');
              return (
                <TableRow key={`lsr-${String(ex.OBJECTID ?? i)}`}>
                  <TableCell>
                    <MapFocusButton
                      layerId={layer.id}
                      exemplar={ex}
                      title={place || 'Storm report'}
                    />
                  </TableCell>
                  <TableCell>{place || '—'}</TableCell>
                  <TableCell>{String(ex.INCIDENT_TYPE || '—')}</TableCell>
                  <TableCell className="tw:text-right tw:tabular-nums">
                    {formatNumberish(ex.MAGNITUDE)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </AnalyticsTable>
      )}
    </PackCardShell>
  );
}

function TemperatureForecastCard({ layer }: { layer: LayerQueryLayerResult }) {
  return (
    <PackCardShell layer={layer}>
      <div className="tw:grid tw:grid-cols-2 tw:gap-2">
        <KpiTile
          label="Highest"
          value={
            typeof layer.stats?.temp_max === 'number'
              ? `${formatNumberish(layer.stats.temp_max, 0)} F`
              : '—'
          }
        />
        <KpiTile
          label="Lowest"
          value={
            typeof layer.stats?.temp_min === 'number'
              ? `${formatNumberish(layer.stats.temp_min, 0)} F`
              : '—'
          }
        />
      </div>
      <GroupBars
        title="Forecast day"
        rows={topGroupRows(layer.groupCounts?.Interval, 3)}
      />
    </PackCardShell>
  );
}

function AccumulationForecastCard({ layer }: { layer: LayerQueryLayerResult }) {
  const isIce = layer.kind === 'ndfdIce';
  return (
    <PackCardShell layer={layer}>
      <div className="tw:grid tw:grid-cols-2 tw:gap-2">
        <KpiTile label="Bands" value={formatCount(layer.totalCount)} />
        <KpiTile
          label={isIce ? 'Highest ice class' : 'Highest snowfall class'}
          value={formatNumberish(layer.stats?.amount_max, 0)}
        />
      </div>
      <GroupBars
        title="Amount"
        rows={topGroupRows(layer.groupCounts?.amount, 6)}
      />
    </PackCardShell>
  );
}

function OutlookCard({ layer }: { layer: LayerQueryLayerResult }) {
  const rows = layer.groupCounts?.label2 ?? layer.groupCounts?.outlook ?? [];
  return (
    <PackCardShell layer={layer}>
      <div className="tw:grid tw:grid-cols-2 tw:gap-2">
        <KpiTile
          label="Highest category"
          value={formatNumberish(layer.stats?.category_max, 0)}
          emphasize={(layer.stats?.category_max ?? 0) >= 4}
        />
        <KpiTile label="Areas" value={formatCount(layer.totalCount)} />
      </div>
      <GroupBars title="Category" rows={topGroupRows(rows, 6)} />
    </PackCardShell>
  );
}

function WssiCard({ layer }: { layer: LayerQueryLayerResult }) {
  const rank = layer.stats?.worst_rank;
  return (
    <PackCardShell layer={layer}>
      <div className="tw:grid tw:grid-cols-2 tw:gap-2">
        <KpiTile
          label="Worst impact"
          value={
            typeof rank === 'number' ? (WSSI_IMPACT_LABELS[rank] ?? '—') : '—'
          }
          emphasize={typeof rank === 'number' && rank >= 3}
        />
        <KpiTile label="Areas" value={formatCount(layer.totalCount)} />
      </div>
      <GroupBars
        title="Impact"
        rows={topGroupRows(layer.groupCounts?.impact, 5)}
        emphasizeValue="EXTREME"
      />
    </PackCardShell>
  );
}

const FEED_KPI_LIMIT = 4;
const FEED_DAILY_ASSETS = 3;
const FEED_DAILY_DAYS = 7;

const TIMELINE_KPI_LABELS: Record<ForecastTimelineFieldName, string> = {
  wind: 'Highest wind',
  gust: 'Highest gust',
  rain: 'Most rain',
  temp: 'Highest temperature',
  temp_min: 'Lowest low',
  temp_max: 'Highest high',
};

/** Indexes where a new UTC day starts, for the slider's tick labels. */
function dayTickIndexes(timeline: ForecastTimeline): number[] {
  if (timeline.stepHours >= 24) {
    const every = timeline.times.length > 8 ? 3 : 1;
    return timeline.times
      .map((_, index) => index)
      .filter((index) => index % every === 0);
  }
  return timeline.times
    .map((iso, index) => (iso.slice(11, 13) === '00' ? index : -1))
    .filter((index) => index >= 0);
}

function SortableHead({
  label,
  field,
  sort,
  onSort,
  className,
}: {
  label: string;
  field: ForecastTimelineSort['field'];
  sort: ForecastTimelineSort;
  onSort: (field: ForecastTimelineSort['field']) => void;
  className?: string;
}) {
  const active = sort.field === field;
  return (
    <TableHead
      className={className}
      aria-sort={
        active ? (sort.order === 'asc' ? 'ascending' : 'descending') : 'none'
      }
    >
      <button
        type="button"
        className="tw:inline-flex tw:items-center tw:gap-0.5 tw:truncate tw:bg-transparent tw:p-0 tw:text-left tw:font-medium hover:tw:text-foreground"
        onClick={() => onSort(field)}
      >
        {label}
        {active ? (sort.order === 'asc' ? ' ↑' : ' ↓') : null}
      </button>
    </TableHead>
  );
}

function ExternalFeedTimelineCard({
  layer,
  timeline,
}: {
  layer: LayerQueryLayerResult;
  timeline: ForecastTimeline;
}) {
  const [step, setStep] = useState(0);
  const [sort, setSort] = useState<ForecastTimelineSort>(() => ({
    field: timelineDefaultSortField(timeline),
    order: 'desc',
  }));
  const lastStep = timeline.times.length - 1;
  const stepIndex = Math.min(step, lastStep);
  const rows = useMemo(
    () => timelineRows(timeline, stepIndex, sort),
    [timeline, stepIndex, sort]
  );
  const extremes = timelineStepExtremes(rows, timeline);
  const conditions = timelineConditionCounts(rows);
  const ticks = dayTickIndexes(timeline);
  const fetchedAt = layer.externalFeed?.fetchedAt;

  const onSort = (field: ForecastTimelineSort['field']) =>
    setSort((current) =>
      current.field === field
        ? { field, order: current.order === 'asc' ? 'desc' : 'asc' }
        : { field, order: field === 'name' ? 'asc' : 'desc' }
    );

  return (
    <PackCardShell layer={layer}>
      {fetchedAt ? (
        <p className="tw:m-0 tw:text-[11px] tw:text-muted-foreground">
          Forecast fetched {formatLayerStatsDate(fetchedAt)} UTC
        </p>
      ) : null}
      <div className="tw:space-y-1">
        <div className="tw:flex tw:items-baseline tw:justify-between tw:gap-2">
          <label
            htmlFor={`forecast-step-${layer.id}`}
            className="tw:text-xs tw:font-medium"
          >
            Forecast time
          </label>
          <span className="tw:text-xs tw:tabular-nums">
            {timelineStepLabel(timeline.times[stepIndex], timeline.stepHours)}
          </span>
        </div>
        <input
          id={`forecast-step-${layer.id}`}
          type="range"
          min={0}
          max={lastStep}
          step={1}
          value={stepIndex}
          onChange={(event) => setStep(Number(event.target.value))}
          className="tw:w-full tw:cursor-pointer tw:accent-sky-600"
          aria-valuetext={timelineStepLabel(
            timeline.times[stepIndex],
            timeline.stepHours
          )}
        />
        <div className="tw:relative tw:h-4 tw:text-[10px] tw:text-muted-foreground">
          {ticks.map((index) => (
            <span
              key={index}
              className="tw:absolute tw:-translate-x-1/2 tw:whitespace-nowrap"
              style={{
                left: `${lastStep > 0 ? (index / lastStep) * 100 : 0}%`,
              }}
            >
              {new Date(timeline.times[index]).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                timeZone: 'UTC',
              })}
            </span>
          ))}
        </div>
      </div>
      <div className="tw:grid tw:grid-cols-2 tw:gap-2">
        <KpiTile label="Assets in area" value={formatCount(rows.length)} />
        {timeline.fields.map((field) => {
          const value = extremes[field.name];
          return (
            <KpiTile
              key={field.name}
              label={TIMELINE_KPI_LABELS[field.name]}
              value={
                value == null
                  ? '—'
                  : `${formatNumberish(value, 1)} ${field.unit}`
              }
            />
          );
        })}
      </div>
      <GroupBars title="Conditions" rows={conditions.slice(0, 6)} />
      <div>
        <p className="tw:mb-1.5 tw:text-xs tw:font-medium">
          Every asset in the area
        </p>
        <div className="tw:max-h-80 tw:overflow-y-auto">
          <AnalyticsTable>
            <TableHeader>
              <TableRow>
                <TableHead className="tw:w-10">Map</TableHead>
                <SortableHead
                  label="Asset"
                  field="name"
                  sort={sort}
                  onSort={onSort}
                />
                {timeline.fields.map((field) => (
                  <SortableHead
                    key={field.name}
                    label={`${field.alias} (${field.unit})`}
                    field={field.name}
                    sort={sort}
                    onSort={onSort}
                  />
                ))}
                <TableHead>Condition</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.resourceSetId}>
                  <TableCell>
                    <MapFocusButton
                      layerId={layer.id}
                      exemplar={{
                        OBJECTID: row.resourceSetId,
                        asset: row.name,
                        latitude: row.lat,
                        longitude: row.lon,
                      }}
                      title={`Forecast for ${row.name}`}
                    />
                  </TableCell>
                  <TableCell title={row.name}>{row.name}</TableCell>
                  {timeline.fields.map((field) => (
                    <TableCell key={field.name} className="tw:tabular-nums">
                      {formatNumberish(
                        row.values[field.name],
                        field.name === 'rain' ? 2 : 0
                      )}
                    </TableCell>
                  ))}
                  <TableCell
                    className="tw:text-xs"
                    title={row.condition ?? undefined}
                  >
                    {row.condition ?? '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </AnalyticsTable>
        </div>
      </div>
    </PackCardShell>
  );
}

function ExternalFeedCard({ layer }: { layer: LayerQueryLayerResult }) {
  const timeline = layer.forecastTimeline;
  if (timeline?.times.length && timeline.assets.length) {
    return <ExternalFeedTimelineCard layer={layer} timeline={timeline} />;
  }
  if (layer.totalCount === 0) {
    return (
      <PackCardShell layer={layer}>
        <p className="tw:m-0 tw:text-sm tw:font-medium">
          No assets to forecast
        </p>
      </PackCardShell>
    );
  }
  return <ExternalFeedSummaryCard layer={layer} />;
}

/** Sections saved before the timeline existed: window maximums and daily tables. */
function ExternalFeedSummaryCard({ layer }: { layer: LayerQueryLayerResult }) {
  const fields = layer.externalFeed?.fields ?? [];
  const kpis = fields
    .filter((field) => field.unit && !field.name.startsWith('min_'))
    .slice(0, FEED_KPI_LIMIT);
  const unitOf = (quantity: 'speed' | 'precip' | 'temp') =>
    fields.find((field) =>
      quantity === 'speed'
        ? /wind|gust/.test(field.name)
        : quantity === 'precip'
          ? field.name.startsWith('rain')
          : /temp/.test(field.name)
    )?.unit ?? '';
  const daily = (layer.dailyForecast ?? []).slice(0, FEED_DAILY_ASSETS);
  const fetchedAt = layer.externalFeed?.fetchedAt;

  return (
    <PackCardShell layer={layer}>
      {fetchedAt ? (
        <p className="tw:m-0 tw:text-[11px] tw:text-muted-foreground">
          Forecast fetched {formatLayerStatsDate(fetchedAt)} UTC
        </p>
      ) : null}
      <div className="tw:grid tw:grid-cols-2 tw:gap-2">
        <KpiTile label="Assets" value={formatCount(layer.totalCount)} />
        {kpis.map((field) => (
          <KpiTile
            key={field.name}
            label={`Highest ${field.alias
              .replace(/^Max /i, '')
              .replace(/^\w/, (c) => c.toLowerCase())}`}
            value={formatNumberish(layer.stats?.[`${field.name}_max`], 1)}
          />
        ))}
      </div>
      {layer.groupCounts?.worst_condition?.length ? (
        <GroupBars
          title="Worst condition"
          rows={topGroupRows(layer.groupCounts.worst_condition, 6)}
        />
      ) : null}
      {daily.map((entry) => (
        <div key={entry.asset}>
          <p className="tw:mb-1.5 tw:text-xs tw:font-medium">{entry.asset}</p>
          <AnalyticsTable>
            <TableHeader>
              <TableRow>
                <TableHead>Day</TableHead>
                <TableHead>Wind {unitOf('speed')}</TableHead>
                <TableHead>Gust {unitOf('speed')}</TableHead>
                <TableHead>Rain {unitOf('precip')}</TableHead>
                <TableHead>Temp {unitOf('temp')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entry.days.slice(0, FEED_DAILY_DAYS).map((day) => (
                <TableRow key={day.date} title={day.condition ?? undefined}>
                  <TableCell>{day.date}</TableCell>
                  <TableCell className="tw:tabular-nums">
                    {formatNumberish(day.max_wind, 0)}
                  </TableCell>
                  <TableCell className="tw:tabular-nums">
                    {formatNumberish(day.max_gust, 0)}
                  </TableCell>
                  <TableCell className="tw:tabular-nums">
                    {formatNumberish(day.rain, 1)}
                  </TableCell>
                  <TableCell className="tw:tabular-nums">
                    {formatNumberish(day.min_temp, 0)}–
                    {formatNumberish(day.max_temp, 0)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </AnalyticsTable>
        </div>
      ))}
    </PackCardShell>
  );
}

function GenericCard({ layer }: { layer: LayerQueryLayerResult }) {
  const aorText = useAorText();
  const exemplars = (layer.exemplars || []).slice(0, 8);
  const groupKey = Object.keys(layer.groupCounts || [])[0];
  const extraKeys = (() => {
    const skip = new Set(['OBJECTID', 'objectid']);
    const keys = new Set<string>();
    for (const row of exemplars) {
      for (const key of Object.keys(row)) {
        if (!skip.has(key)) keys.add(key);
      }
    }
    return [...keys].slice(0, 3);
  })();
  const displayKey = extraKeys[0];
  const attrKeys = extraKeys.slice(1, 3);

  return (
    <Card size="sm" className="tw:h-full">
      <CardHeader className="tw:border-b tw:border-border tw:pb-(--card-spacing)">
        <CardTitle className="tw:text-sm">{layer.name}</CardTitle>
      </CardHeader>
      <CardContent className="tw:space-y-3">
        <p className="tw:text-[11px] tw:text-muted-foreground">
          Dedicated Analytics pack not available for this layer.
        </p>
        <div className="tw:grid tw:grid-cols-2 tw:gap-2">
          <KpiTile
            label={aorText('Features in AOR')}
            value={formatCount(layer.totalCount)}
          />
        </div>
        {layer.notes?.some((n) => n.includes('unscoped')) ? (
          <p className="tw:text-[11px] tw:text-muted-foreground">
            Feature count is unscoped (no AOR bounds).
          </p>
        ) : null}
        {groupKey ? (
          <GroupBars
            title={groupKey}
            rows={topGroupRows(layer.groupCounts?.[groupKey], 8)}
          />
        ) : null}
        {exemplars.length > 0 && (
          <div>
            <p className="tw:mb-1.5 tw:text-xs tw:font-medium">
              Sample features
            </p>
            <AnalyticsTable>
              <TableHeader>
                <TableRow>
                  <TableHead className="tw:w-10">Map</TableHead>
                  <TableHead>{displayKey || 'Feature'}</TableHead>
                  <TableHead>OBJECTID</TableHead>
                  {attrKeys.map((key) => (
                    <TableHead key={key}>{key}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {exemplars.map((ex, i) => (
                  <TableRow key={`gen-${String(ex.OBJECTID ?? i)}`}>
                    <TableCell>
                      <MapFocusButton
                        layerId={layer.id}
                        exemplar={ex}
                        title={String(
                          displayKey ? ex[displayKey] : ex.OBJECTID || 'Feature'
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      {displayKey ? String(ex[displayKey] ?? '—') : '—'}
                    </TableCell>
                    <TableCell className="tw:tabular-nums">
                      {String(ex.OBJECTID ?? '—')}
                    </TableCell>
                    {attrKeys.map((key) => (
                      <TableCell key={key} className="tw:text-xs">
                        {String(ex[key] ?? '—')}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </AnalyticsTable>
          </div>
        )}
        <QueryErrorList layer={layer} />
      </CardContent>
    </Card>
  );
}

function AnalyticsPackCard({
  kind,
  layer,
  floodFilters,
  onFloodFiltersChange,
  filtersDisabled,
}: {
  kind: ReturnType<typeof analyticsCardsToRender>[number]['kind'];
  layer: LayerQueryLayerResult;
  floodFilters: FloodWidgetFilters;
  onFloodFiltersChange?: (next: FloodWidgetFilters) => void;
  filtersDisabled?: boolean;
}) {
  switch (kind) {
    case 'floodingBins':
      return (
        <FloodCard
          layer={layer}
          floodFilters={floodFilters}
          onFloodFiltersChange={onFloodFiltersChange}
          filtersDisabled={filtersDisabled}
        />
      );
    case 'tingOutages':
      return <PowerCard layer={layer} />;
    case 'precipTotal':
      return <PrecipCard layer={layer} />;
    case 'nwsWatches':
      return <NwsCard layer={layer} />;
    case 'hurricaneCone':
      return <HurricaneConeCard layer={layer} />;
    case 'hurricaneForecast':
      return <HurricaneForecastCard layer={layer} />;
    case 'hurricaneObserved':
      return <HurricaneObservedCard layer={layer} />;
    case 'streamGauges':
      return <StreamGaugesCard layer={layer} />;
    case 'wildfirePerimeters':
      return <WildfirePerimetersCard layer={layer} />;
    case 'wildfireIncidents':
      return <WildfireIncidentsCard layer={layer} />;
    case 'earthquakes':
      return <EarthquakesCard layer={layer} />;
    case 'shakeIntensity':
      return <ShakeIntensityCard layer={layer} />;
    case 'thermalHotspots':
      return <ThermalHotspotsCard layer={layer} />;
    case 'windGust':
      return <WindGustCard layer={layer} />;
    case 'hurricaneWindProbability':
      return <HurricaneWindProbabilityCard layer={layer} />;
    case 'airQuality':
      return <AirQualityCard layer={layer} />;
    case 'wildBirdAvianInfluenza':
      return <WildBirdAvianInfluenzaCard layer={layer} />;
    case 'metarStations':
      return <MetarCard layer={layer} />;
    case 'stormReports':
      return <StormReportsCard layer={layer} />;
    case 'ndfdTemperature':
      return <TemperatureForecastCard layer={layer} />;
    case 'ndfdSnowfall':
    case 'ndfdIce':
      return <AccumulationForecastCard layer={layer} />;
    case 'spcOutlook':
    case 'wpcExcessiveRainfall':
      return <OutlookCard layer={layer} />;
    case 'wssi':
      return <WssiCard layer={layer} />;
    case 'externalFeed':
      return <ExternalFeedCard layer={layer} />;
    default:
      return <GenericCard layer={layer} />;
  }
}

function PowerCard({ layer }: { layer: LayerQueryLayerResult }) {
  const recoveryMin = layer.stats?.recoveredPercentage_min;
  const exemplars = (layer.exemplars || []).slice(0, 8);

  return (
    <Card size="sm" className="tw:h-full">
      <CardHeader className="tw:border-b tw:border-border tw:pb-(--card-spacing)">
        <CardTitle className="tw:text-sm">
          Power impact
          <span className="tw:ml-2 tw:text-xs tw:font-normal tw:text-muted-foreground">
            {layer.name}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="tw:space-y-3">
        <div className="tw:grid tw:grid-cols-2 tw:gap-2">
          <KpiTile label="Outages" value={formatCount(layer.totalCount)} />
          <KpiTile
            label="Avg recovery"
            value={formatRecoveryPercent(layer.stats?.recoveredPercentage_avg)}
          />
          <KpiTile
            label="Min recovery"
            value={formatRecoveryPercent(recoveryMin)}
            emphasize={typeof recoveryMin === 'number' && recoveryMin === 0}
          />
          <KpiTile
            label="Max recovery"
            value={formatRecoveryPercent(layer.stats?.recoveredPercentage_max)}
          />
        </div>

        <p className="tw:text-[10px] tw:text-muted-foreground">
          Area values are service units as returned by ArcGIS (not converted to
          mi²).
        </p>

        {exemplars.length > 0 && (
          <div>
            <p className="tw:mb-1.5 tw:text-xs tw:font-medium">
              Stuck / largest outages
            </p>
            <AnalyticsTable>
              <TableHeader>
                <TableRow>
                  <TableHead className="tw:w-10">Map</TableHead>
                  {!LAYER_STATS_DEMO_FILTERS_ENABLED ? (
                    <TableHead>Event</TableHead>
                  ) : null}
                  <TableHead>State</TableHead>
                  <TableHead className="tw:text-right">Recovery</TableHead>
                  <TableHead className="tw:text-right">Area</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {exemplars.map((ex, i) => (
                  <TableRow
                    key={`ting-ex-${String(ex.OBJECTID ?? ex.eventId ?? 'row')}-${i}`}
                  >
                    <TableCell>
                      <MapFocusButton
                        layerId={layer.id}
                        exemplar={ex}
                        title={`Outage ${ex.eventId ?? ex.OBJECTID ?? ''}`}
                      />
                    </TableCell>
                    {!LAYER_STATS_DEMO_FILTERS_ENABLED ? (
                      <TableCell className="tw:tabular-nums">
                        {String(ex.eventId ?? '—')}
                      </TableCell>
                    ) : null}
                    <TableCell>
                      <Badge
                        variant={
                          String(ex.powerState).toLowerCase() === 'outage'
                            ? 'orange'
                            : 'secondary'
                        }
                      >
                        {String(ex.powerState || '—')}
                      </Badge>
                    </TableCell>
                    <TableCell className="tw:text-right tw:tabular-nums">
                      {formatRecoveryPercent(ex.recoveredPercentage)}
                    </TableCell>
                    <TableCell className="tw:text-right tw:tabular-nums">
                      {typeof ex.Shape__Area === 'number'
                        ? ex.Shape__Area.toFixed(4)
                        : '—'}
                    </TableCell>
                    <TableCell className="tw:whitespace-nowrap tw:text-xs">
                      {formatOutageDisplayDate(ex.displaydate)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </AnalyticsTable>
          </div>
        )}

        {layer.queryErrors && layer.queryErrors.length > 0 && (
          <div className="tw:text-xs tw:text-destructive">
            <strong>Query errors</strong>
            <ul className="tw:mb-0 tw:mt-1">
              {layer.queryErrors.map((e) => (
                <li key={e.query}>
                  {e.query}: {e.error}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function LayerStatsResultsView({
  payload,
  floodFilters,
  onFloodFiltersChange,
  filtersDisabled = false,
  onRankingChange,
}: {
  payload: LayerStatsAutomatedPayload;
  floodFilters?: FloodWidgetFilters | null;
  onFloodFiltersChange?: (next: FloodWidgetFilters) => void;
  filtersDisabled?: boolean;
  /** Omitted in read-only views, which hides the ranked table's controls. */
  onRankingChange?: (next: LayerRankingSetting) => void;
}) {
  const resolvedFloodFilters = normalizeFloodWidgetFilters(floodFilters);
  const results = payload.LayerQueryResults;
  const notes = Array.isArray(payload.notes) ? payload.notes : [];
  const layers = results?.layers || [];
  const cards = analyticsCardsToRender(layers);
  const label = windowLabel(results);
  if (payload.error) {
    return (
      <p className="tw:mb-2 tw:text-sm tw:text-destructive">
        <strong>Analytics error:</strong> {payload.error}
      </p>
    );
  }

  if (!layers.length) {
    return (
      <div>
        <p className="tw:mb-2 tw:text-sm tw:text-muted-foreground">
          No layer stats packs matched the selected Layers.
        </p>
        {notes.length > 0 && (
          <ul className="tw:mb-0 tw:text-sm tw:text-muted-foreground">
            {notes.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  return (
    <div className="tw:space-y-3">
      {label && (
        <p className="tw:text-sm">
          <strong>Window:</strong> {label}
        </p>
      )}
      {notes.map((n) => (
        <p key={n} className="tw:text-xs tw:text-muted-foreground">
          {n}
        </p>
      ))}

      <div className="tw:grid tw:grid-cols-1 tw:gap-3 lg:tw:grid-cols-2">
        {cards.map(({ kind, layer }) => (
          <div
            key={`${kind}:${layer.id || layer.name}`}
            className={cn(
              'tw:flex tw:min-w-0 tw:flex-col tw:gap-3',
              // Pack cards are h-full; next to a second table that stretches them to both heights.
              (layer.assetExposure || layer.ranking) &&
                'tw:[&>[data-slot=card]]:h-auto'
            )}
          >
            <AnalyticsPackCard
              kind={kind}
              layer={layer}
              floodFilters={resolvedFloodFilters}
              onFloodFiltersChange={onFloodFiltersChange}
              filtersDisabled={filtersDisabled}
            />
            {layer.assetExposure ? (
              <AssetExposureTable
                block={layer.assetExposure}
                layerId={layer.id}
                layerName={layer.name}
              />
            ) : layer.ranking ? (
              <RankedFeaturesTable
                block={layer.ranking}
                layerId={layer.id}
                layerName={layer.name}
                onChange={onRankingChange}
                disabled={filtersDisabled}
              />
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}
